import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { DESKTOP, useMediaQuery } from '../hooks/useMediaQuery';
import { useScreenData } from '../hooks/useScreenData';
import RecordList, { Pager, ViewSwitch } from '../components/RecordList';
import { EmptyState, InlineError, ReadError, LoadingState, StaleNotice } from '../components/PageStates';
import { formatUtc } from './auditFields';
import './Lists.css';

// Exactly the filters and sorts server/schemas/platform-tenant.schemas.js accepts; nothing else is sent.
const PLANS = [['', 'Any plan'], ['free', 'Free'], ['basic', 'Basic'], ['premium', 'Premium']];
const STATUSES = [['', 'Any status'], ['true', 'Active'], ['false', 'Locked']];
const SORTS = [
  ['name:asc', 'Name, A to Z'], ['name:desc', 'Name, Z to A'], ['created_at:desc', 'Newest first'],
  ['created_at:asc', 'Oldest first'], ['user_count:desc', 'Most enabled members'], ['user_count:asc', 'Fewest enabled members'],
];
const LIMIT = 20;
// The schema caps search at 255 characters. A longer one is never sent: a refused query retried
// as-is fails forever (auditFields.js).
const MAX_SEARCH = 255;

/** Every field the tenant list returns (platformTenant.service.js listTenants). */
export const TENANT_COLUMNS = [
  ['Tenant', (t) => <Link to={`/tenants/${t.id}`}>{t.name}</Link>],
  ['Tenant ID', (t) => <span className="record-id">{t.id}</span>],
  ['Slug', (t) => t.slug],
  ['Plan', (t) => <span className="badge badge-plan">{t.plan}</span>],
  // The server counts only enabled members (u.is_active = true), so the label says so.
  ['Enabled members', (t) => <span className="record-count">{t.user_count}</span>],
  ['Member limit', (t) => t.max_users == null ? 'Not set' : <span className="record-count">{t.max_users}</span>],
  ['Status', (t) => (t.is_active ? <span className="badge badge-active">Active</span> : (
    <>
      <span className="badge badge-locked">Locked</span>
      {t.locked_at && <> since <time dateTime={t.locked_at}>{formatUtc(t.locked_at)}</time></>}
    </>
  ))],
  ['Created', (t) => <time dateTime={t.created_at}>{formatUtc(t.created_at)}</time>],
];

function paramsOf({ page, search, plan, isActive, sort }) {
  const [sortBy, sortOrder] = sort.split(':');
  const params = { page, limit: LIMIT, sortBy, sortOrder };
  if (search.trim()) params.search = search.trim();
  if (plan) params.plan = plan;
  if (isActive) params.isActive = isActive;
  return params;
}

export default function TenantList() {
  const [tableOnNarrow, setTableOnNarrow] = useState(false);
  const wide = useMediaQuery(DESKTOP);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState({ page: 1, search: '', plan: '', isActive: '', sort: 'name:asc' });
  const debounce = useRef(null);
  const ids = useId();
  useEffect(() => () => clearTimeout(debounce.current), []);

  const params = paramsOf(query);
  const tooLong = (params.search ?? '').length > MAX_SEARCH;
  const list = useScreenData(JSON.stringify(params), (options) => api.listTenants(params, options),
    { enabled: !tooLong, keeps: (data) => data.tenants.length > 0 });
  const filtered = Boolean(params.search || params.plan || params.isActive);

  // A filter change is a new query: it starts again at page 1.
  const change = (patch) => setQuery((q) => ({ ...q, ...patch, page: 1 }));
  const onSearch = (e) => {
    const { value } = e.target;
    setSearch(value);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => change({ search: value }), 300);
  };
  const select = (name, label, options) => (
    <div className="field">
      <label htmlFor={`${ids}${name}`}>{label}</label>
      <select id={`${ids}${name}`} value={query[name]} onChange={(e) => change({ [name]: e.target.value })}>
        {options.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
      </select>
    </div>
  );

  const filterFields = <>
    {select('plan', 'Plan', PLANS)}
    {select('isActive', 'Status', STATUSES)}
    {select('sort', 'Sort', SORTS)}
  </>;
  const data = list.data;
  const page = data?.page ?? query.page;
  const pages = data ? Math.max(1, Math.ceil(data.total / (data.limit || LIMIT))) : 1;
  return (
    <div className="list-page">
      <h2>Tenants</h2>

      <form className="list-filters" aria-label="Tenant filters" onSubmit={(e) => e.preventDefault()}>
        <div className="field">
          <label htmlFor={`${ids}search`}>Search tenants</label>
          <input id={`${ids}search`} type="search" placeholder="Search by name or slug" value={search} onChange={onSearch} />
        </div>
        {wide ? filterFields : (
          <details className="list-more-filters" open={Boolean(query.plan || query.isActive || query.sort !== 'name:asc')}>
            <summary>More filters</summary>
            {filterFields}
          </details>
        )}
      </form>

      <ViewSwitch tableOnNarrow={tableOnNarrow} onChange={setTableOnNarrow} />
      {list.loading && <LoadingState label="Loading tenants" lines={6} />}
      {tooLong && <InlineError title="Check the search" message={`A search can be at most ${MAX_SEARCH} characters.`} />}
      {list.error && <ReadError error={list.error} onRetry={list.reload} />}
      {list.stale && <StaleNotice loadedAt={list.loadedAt} reason={list.stale} onReload={list.reload} />}
      {/* The count and the page are two statements, so a page past the end can come back empty. */}
      {data && data.tenants.length === 0 && (data.total > 0
        ? <EmptyState title="No tenants on this page">This page is past the end of the list.</EmptyState>
        : filtered
          ? <EmptyState title="No tenants match">No tenant matches this search and these filters.</EmptyState>
          : <EmptyState title="No tenants yet">No household has signed up.</EmptyState>)}
      {data && data.tenants.length > 0 && (
        <>
          <p className="list-summary"><span className="record-count">{data.total}</span> {data.total === 1 ? 'tenant' : 'tenants'}{filtered ? ' match' : ''}</p>
          <RecordList tableOnNarrow={tableOnNarrow} label="Tenants" caption="Tenants, one row each" columns={TENANT_COLUMNS} records={data.tenants} recordKey={(t) => t.id} />
        </>
      )}
      {data && (data.tenants.length > 0 || pages > 1 || page > 1) && (
        <Pager page={page} pages={pages} disabled={Boolean(list.stale)} onPage={(next) => setQuery((q) => ({ ...q, page: next }))} />
      )}
    </div>
  );
}
